import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { applyOpenTalentJob } from "../../../../redux/features/find-a-job/actions/authAction";
import PdfOrImageUploader from "../../../pdfImage uploader";
import ErrorMessage from "../../../form/ErrorMessage";
import TagInput from "../../../form/TagInput";
import { getProfile } from "../../../../utils/pip";
import "./ApplyNowSignupModal.css";

const experienceOptions = ["0-2 years", "3-5 years", "6-10 years", "10+ years"];

const splitList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const getInitialLocation = (openTalent = {}, profileData = {}) => {
  if (String(openTalent?.location || "").trim()) return String(openTalent.location).trim();
  const city = String(openTalent?.city || profileData?.city || "").trim();
  const country = String(openTalent?.country || profileData?.country || "").trim();
  return [city, country].filter(Boolean).join(", ");
};

const normalizeSingleAvailability = (value) => {
  const resolved = splitList(value);
  return resolved[0] || "";
};

const normalizeDateInputValue = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const ApplyNowPrefilledModal = ({
  isOpen = true,
  onClose,
  onSubmit,
  onOpenSignIn,
  formData = {},
}) => {
  const dispatch = useDispatch();
  const { isLoading, freelancerProfileData } = useSelector(
    (state) => state?.freelancer?.auth || {}
  );
  const profileData =
    freelancerProfileData && Object.keys(freelancerProfileData).length
      ? freelancerProfileData
      : getProfile("freelancer") || {};
  const openTalent = profileData?.open_talent || {};
  const cvValue = profileData?.cv || openTalent?.cv;
  const existingCvList = Array.isArray(cvValue) ? cvValue : cvValue ? [cvValue] : [];
  const [formValues, setFormValues] = React.useState({
    full_name: formData?.full_name || profileData?.full_name || "",
    email: formData?.email || profileData?.email || "",
    phone_number: openTalent?.phone_number || profileData?.phone_number || "+91",
    location: getInitialLocation(openTalent, profileData),
    years_of_experience: openTalent?.years_of_experience || "",
    availability: normalizeDateInputValue(normalizeSingleAvailability(openTalent?.availability)),
    skills: splitList(openTalent?.skills || openTalent?.key_skills),
    linkedin_url:
      openTalent?.linkedin_profile || profileData?.linkedin_url || "",
    portfolio_url:
      openTalent?.website_or_portfolio ||
      openTalent?.portfolio_website ||
      profileData?.portfolio_url ||
      "",
    cover_letter: "",
    resume_files: existingCvList,
  });
  const [formErrors, setFormErrors] = React.useState({});
  const [apiError, setApiError] = React.useState("");

  const handleOverlayClick = (event) => {
    event.stopPropagation();
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  useEffect(() => {
    setFormValues((prev) => ({
      ...prev,
      full_name: prev?.full_name || formData?.full_name || profileData?.full_name || "",
      email: prev?.email || formData?.email || profileData?.email || "",
      phone_number:
        prev?.phone_number ||
        openTalent?.phone_number ||
        profileData?.phone_number ||
        "+91",
      location: prev?.location || getInitialLocation(openTalent, profileData),
      years_of_experience:
        prev?.years_of_experience || openTalent?.years_of_experience || "",
      availability:
        prev?.availability ||
        normalizeDateInputValue(normalizeSingleAvailability(openTalent?.availability)),
      skills:
        prev?.skills ||
        splitList(openTalent?.skills || openTalent?.key_skills),
      linkedin_url:
        prev?.linkedin_url ||
        openTalent?.linkedin_profile ||
        profileData?.linkedin_url ||
        "",
      portfolio_url:
        prev?.portfolio_url ||
        openTalent?.website_or_portfolio ||
        openTalent?.portfolio_website ||
        profileData?.portfolio_url ||
        "",
      resume_files: prev?.resume_files?.length ? prev.resume_files : existingCvList,
    }));
  }, [existingCvList, formData?.email, formData?.full_name, openTalent, profileData]);

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  const resolveResumeValue = (resumeFiles = []) => {
    const firstItem = resumeFiles?.[0];
    if (!firstItem) return null;
    if (firstItem instanceof File) return firstItem;
    if (typeof firstItem === "string") return firstItem;
    if (firstItem?.file instanceof File) return firstItem.file;
    if (typeof firstItem?.file === "string") return firstItem.file;
    if (typeof firstItem?.url === "string") return firstItem.url;
    if (typeof firstItem?.preview === "string") return firstItem.preview;
    return null;
  };

  const validateForm = () => {
    const errors = {};
    const checkUrl = (value) => {
      try {
        // eslint-disable-next-line no-new
        new URL(value);
        return true;
      } catch (e) {
        return false;
      }
    };

    if (!formValues.phone_number) {
      errors.phone_number = "Please enter phone number";
    } else if (!isValidPhoneNumber(formValues.phone_number)) {
      errors.phone_number = "Please enter valid phone number";
    }

    if (!String(formValues.location || "").trim()) {
      errors.location = "Please enter your location";
    }

    if (!String(formValues.years_of_experience || "").trim()) {
      errors.years_of_experience = "Please select experience";
    }

    if (!String(formValues.availability || "").trim()) {
      errors.availability = "Please select availability";
    }

    if (!Array.isArray(formValues.skills) || formValues.skills.length === 0) {
      errors.skills = "Please enter skills";
    }

    if (
      String(formValues.linkedin_url || "").trim() &&
      !checkUrl(String(formValues.linkedin_url || "").trim())
    ) {
      errors.linkedin_url = "Please enter valid Linkedin profile link";
    }

    if (
      String(formValues.portfolio_url || "").trim() &&
      !checkUrl(String(formValues.portfolio_url || "").trim())
    ) {
      errors.portfolio_url = "Please enter valid portfolio link";
    }

    if (!String(formValues.cover_letter || "").trim()) errors.cover_letter = "Please enter cover letter";
    if (!Array.isArray(formValues.resume_files) || formValues.resume_files.length === 0) {
      errors.resume_files = "Please upload resume";
    }

    return errors;
  };

  const updateField = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => ({ ...prev, [key]: "" }));
    setApiError("");
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);
    setApiError("");
    if (Object.keys(errors).length) return;

    const payload = new FormData();
    payload.append("job_id", String(formData?.job_id || ""));
    payload.append("full_name", String(formValues.full_name || ""));
    payload.append("email", String(formValues.email || ""));
    payload.append("phone_number", String(formValues.phone_number || ""));
    payload.append("location", String(formValues.location || ""));
    payload.append("years_of_experience", String(formValues.years_of_experience || ""));
    payload.append("availability", String(formValues.availability || ""));
    payload.append("skills", (formValues.skills || []).join(", "));
    payload.append("linkedin_url", String(formValues.linkedin_url || ""));
    payload.append("portfolio_url", String(formValues.portfolio_url || ""));
    payload.append("cover_letter", String(formValues.cover_letter || "")); 
    payload.append("work_type", String(formValues.availability || ""));
    payload.append("current_position", String(formValues.location || ""));
    const resumeValue = resolveResumeValue(formValues.resume_files);
    if (resumeValue) {
      payload.append("resume_url", resumeValue);
    }

    dispatch(
      applyOpenTalentJob({
        payload,
        token: formData?.auth_token || "",
        callback: (response) => {
          const isSuccess = response?.success === true || response?.status === true;
          if (!isSuccess) {
            setApiError(response?.message || "Failed to submit application.");
            return;
          }
          if (isSuccess && onSubmit) {
            onSubmit(response);
          }
        },
      })
    );
  };

  return createPortal(
    <div className="job-modal-overlay" onClick={handleOverlayClick}>
      <div className="job-modal job-apply-modal" role="dialog" aria-modal="true">
        <div className="job-modal-header">
          <h2 className="job-modal-title ct_fs_30">
            {formData?.job_title || "[Job Title]"} .{" "}
            {formData?.company_name || "[Company Name]"}
          </h2>

          <button
            type="button"
            className="job-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4.66699 4.66699L11.3337 11.3337M11.3337 4.66699L4.66699 11.3337"
                stroke="#023047"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <form className="job-modal-body" onSubmit={handleSubmit}>
          <div className="job-grid">
            <div className="job-field">
              <label className="job-label">phone number</label>
              <div className="job-phone-row">
                <PhoneInput
                  id="phone_number"
                  className="form-control ct_input ct_phone_input_no_14 job-phone-input-wrap"
                  defaultCountry="IN"
                  international
                  countryCallingCodeEditable
                  value={formValues.phone_number}
                  onChange={(value) => updateField("phone_number", value || "")}
                />
              </div>
              {formErrors.phone_number ? (
                <p className="ct_modal_input_error">{formErrors.phone_number}</p>
              ) : null}
            </div>

            <div className="job-field">
              <label className="job-label">Your location</label>
              <input
                className="job-input"
                type="text"
                placeholder="Enter location"
                value={formValues.location}
                onChange={(event) => updateField("location", event.target.value)}
              />
              {formErrors.location ? (
                <p className="ct_modal_input_error">{formErrors.location}</p>
              ) : null}
            </div>
          </div>

          <div className="job-grid">
            <div className="job-field">
              <label className="job-label">Experience</label>
              <div className="job-select-wrap">
                <select
                  value={formValues.years_of_experience}
                  className="job-input job-select"
                  onChange={(event) => updateField("years_of_experience", event.target.value)}
                >
                  <option value="">Select experience</option>
                  {experienceOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <span className="job-chevron">
                  <svg width="14" height="9" viewBox="0 0 14 9" fill="none">
                    <path
                      d="M1.16699 1.25L7.00033 7.08333L12.8337 1.25"
                      stroke="#023047"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </div>
              {formErrors.years_of_experience ? (
                <p className="ct_modal_input_error">{formErrors.years_of_experience}</p>
              ) : null}
            </div>
            <div className="job-field">
              <label className="job-label">Your availability</label>
              <div className="job-date-wrap">
                <input
                  id="availability_date"
                  className="job-input job-date-input"
                  type="date"
                  value={formValues.availability}
                  onChange={(event) => updateField("availability", event.target.value)}
                />
              </div>
              {formErrors.availability ? (
                <p className="ct_modal_input_error">{formErrors.availability}</p>
              ) : null}
            </div>
          </div>

          <TagInput
            label="Skills"
            placeholder="Type to add skills..."
            name="skills"
            value={formValues.skills}
            onChange={updateField}
            error={formErrors.skills}
            addOnBlur={true}
          />

          <div className="job-field job-field-full">
            <label className="job-label">Linkedin Profile Link (Optional)</label>
            <input
              className="job-input"
              type="url"
              value={formValues.linkedin_url}
              onChange={(event) => updateField("linkedin_url", event.target.value)}
            />
            {formErrors.linkedin_url ? (
              <p className="ct_modal_input_error">{formErrors.linkedin_url}</p>
            ) : null}
          </div>

          <div className="job-field job-field-full">
            <label className="job-label">Portfolio Link (Optional)</label>
            <input
              className="job-input"
              type="url"
              value={formValues.portfolio_url}
              onChange={(event) => updateField("portfolio_url", event.target.value)}
            />
            {formErrors.portfolio_url ? (
              <p className="ct_modal_input_error">{formErrors.portfolio_url}</p>
            ) : null}
          </div>

          <div className="job-field job-field-full">
            <label className="job-label">Cover Letter</label>
            <textarea
              className="job-input job-textarea h-auto" rows={4}
              value={formValues.cover_letter}
              onChange={(event) => updateField("cover_letter", event.target.value)}
            />
            {formErrors.cover_letter ? (
              <p className="ct_modal_input_error">{formErrors.cover_letter}</p>
            ) : null}
          </div>

          <div className="job-field job-field-full">
            <label className="job-label">Upload Resume</label>
            <PdfOrImageUploader
              maxFiles={1}
              multiple={false}
              acceptType="application/pdf,.pdf"
              placeholder="Upload CV (PDF only)"
              replaceOnSelect={true}
              existingFiles={
                formValues?.resume_files && Array.isArray(formValues.resume_files)
                  ? formValues.resume_files.map((file, idx) => ({
                    id: file?.id || idx,
                    file: file,
                    name:
                      file?.name ||
                      (typeof file === "string"
                        ? file.split("/").pop()
                        : `cv-${idx + 1}.pdf`),
                    type: "pdf",
                    preview:
                      typeof file === "string"
                        ? file
                        : file?.preview || file?.url || "",
                    isExisting: !(file instanceof File),
                  }))
                  : []
              }
              onChange={(newFiles) => updateField("resume_files", newFiles)}
            />
            <ErrorMessage
              errors={formErrors}
              touched={{ resume_files: true }}
              fieldName="resume_files"
            />
          </div>

          {apiError ? <p className="ct_modal_input_error mt-1">{apiError}</p> : null}

          <div className="job-submit-wrap">
            <button className="job-submit-btn" type="submit" disabled={isLoading}>
              {isLoading ? "Please wait..." : "Submit"}
            </button>
          </div>

          <p className="job-footer-note">
            Increase your chances by <span className="job-orange">3x</span> with a{" "}
            <span
              className="job-yellow"
              style={{ cursor: "pointer" }}
              onClick={() => onOpenSignIn?.()}
            >
              NUVIO profile
            </span>
          </p>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ApplyNowPrefilledModal;
